import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCpu, FiShield, FiEye } = FiIcons;

const pillars = [
  { icon: FiEye, title: 'AI Transparency & Accountability', desc: 'Ensure your AI systems can be audited, explained, and justified to regulators, customers, and stakeholders.' },
  { icon: FiShield, title: 'AI Security', desc: 'Protect your models from adversarial attacks, data poisoning, and unintended behaviour with purpose-built security controls.' },
  { icon: FiCpu, title: 'AI Governance', desc: 'Build internal policies, oversight structures, and accountability frameworks so your AI deployments stay ethical and under control.' },
];

export default function AIGovernance() {
  return (
    <section id="ai-governance" className="pt-24 pb-24 md:pt-32 md:pb-32 bg-[#111111] border-t border-[#222222]">
      <div className="container mx-auto px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-brand-orange/10 border border-brand-orange/30 text-brand-orange text-sm font-semibold mb-6 tracking-wider uppercase">
            <SafeIcon icon={FiCpu} className="text-base" />
            New Service
          </span>
          <h5 className="text-brand-orange font-bold tracking-widest uppercase text-sm mb-4">AI Management & Governance</h5>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Govern Your AI Before It<br />
            <span className="text-brand-orange">Governs You</span>
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            AI is transforming every industry — but without proper governance and security, it introduces serious risks.
            Minotaur Solutions helps you deploy AI responsibly, keeping it transparent, secure, and fully under your control.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-[#1a1a1a] border border-[#2a2a2a] hover:border-brand-orange/40 p-10 rounded-2xl transition-all duration-300 group"
            >
              <div className="w-14 h-14 rounded-xl bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform duration-300">
                <SafeIcon icon={pillar.icon} className="text-2xl text-brand-orange" />
              </div>
              <h3 className="text-lg font-bold text-white mb-4">{pillar.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{pillar.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl border border-brand-orange/30 bg-[#1a1a1a] p-12 md:p-16 flex flex-col md:flex-row items-center justify-between gap-10"
        >
          <div className="text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Is your AI deployment secure and compliant?
            </h3>
            <p className="text-gray-400 max-w-xl">
              Don't wait for something to go wrong. Book a free AI governance assessment today.
            </p>
          </div>
          <div className="flex-shrink-0">
            <a href="#contact" className="btn-primary whitespace-nowrap">
              Get Free AI Assessment
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}